import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiAlertTriangle, FiUsers, FiHome } = FiIcons;

const ProblemSection = () => {
  const stats = [
    { number: '4 Lakh', label: 'People Begging in India', icon: FiUsers },
    { number: '80%', label: 'Willing to Work if Given Opportunity', icon: FiHome },
    { number: '₹50-200', label: 'Daily Earnings from Begging', icon: FiAlertTriangle }
  ];

  return (
    <section className="py-20 bg-gray-50 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-20 w-96 h-96 bg-red-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-orange-500 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left - Emotional Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="The reality of begging in India"
                className="w-full h-96 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
              
              {/* Overlay Text */}
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <div className="text-lg font-semibold mb-2">This is not a choice...</div>
                <div className="text-sm text-gray-200">It's a cycle of poverty that needs to be broken</div>
              </div>
            </div>

            {/* Floating Stat Card */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ repeat: Infinity, duration: 3 }}
              className="absolute -top-6 -right-6 bg-red-600 text-white rounded-xl p-4 shadow-2xl"
            >
              <div className="text-2xl font-bold">Every 30 Seconds</div>
              <div className="text-sm">Someone New Starts Begging</div>
            </motion.div>
          </motion.div>

          {/* Right - Problem Statement */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div className="mb-8">
              <div className="inline-flex items-center space-x-2 bg-red-100 text-red-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <SafeIcon icon={FiAlertTriangle} className="w-4 h-4" />
                <span>The Harsh Reality</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                The Problem is
                <span className="block text-red-600">Not Poverty</span>
              </h2>
              
              <div className="space-y-6 text-lg text-gray-700">
                <p className="leading-relaxed">
                  <strong>Every day, millions beg at traffic signals and railway stations across India.</strong> But here's what most people don't know: <span className="text-red-600 font-semibold">80% of them are willing to work</span> if given shelter, food, and basic skills.
                </p>
                
                <p className="leading-relaxed">
                  The real problem isn't laziness or choice — it's the <strong>lack of opportunity, shelter, and dignity</strong>. Without an address, they can't get jobs. Without skills, they can't earn sustainable income.
                </p>
                
                <p className="leading-relaxed">
                  <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded font-medium">
                    This is where we come in.
                  </span>
                </p>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 gap-4">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-center space-x-4 bg-white rounded-xl p-6 shadow-lg border-l-4 border-red-500"
                >
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <SafeIcon icon={stat.icon} className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">{stat.number}</div>
                    <div className="text-gray-600">{stat.label}</div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Call to Action */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              viewport={{ once: true }}
              className="mt-8 bg-gradient-to-r from-red-600 to-orange-600 rounded-2xl p-6 text-white text-center"
            >
              <div className="text-xl font-bold mb-2">But There's Hope</div>
              <div className="mb-4">We've already transformed 2,547+ lives. Your support can help us reach 500 more in the next 6 months.</div>
              <button className="bg-white text-red-600 px-8 py-3 rounded-full font-bold hover:bg-gray-100 transition-colors duration-200">
                See How We Do It →
              </button>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;